import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./ProductManager.css";
import { useSelector } from "react-redux";

function ProductValises({ onProductAdded, onSubProductAdded }) {
  const [newProductName, setNewProductName] = useState("");
  const [newProductCategory, setNewProductCategory] = useState("VALISES");
  const [newSubProductName, setNewSubProductName] = useState("");
  const [newSubProductPrice, setNewSubProductPrice] = useState("");
  const [newSubProductBarcode, setNewSubProductBarcode] = useState("");
  const [newSubProductImage, setNewSubProductImage] = useState(null);
  const [newSubProductImagePreview, setNewSubProductImagePreview] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // États pour la création de SET (composition GRAND / MOYEN / PETIT)
  const [setPrice, setSetPrice] = useState("");
  const [allSubProductsForSet, setAllSubProductsForSet] = useState([]);
  const [selectedSetSubProducts, setSelectedSetSubProducts] = useState([]);
  const [setImageFile, setSetImageFile] = useState(null);
  const [setImagePreview, setSetImagePreview] = useState(null); // data URL pour aperçu
  const setImageInputRef = useRef(null); // pour réinitialiser l'input file après "Retirer" ou après création
  const setImageFileRef = useRef(null); // ref pour lire le fichier au clic (évite closure stale)
  const subProductImageInputRef = useRef(null);

  const currentUser = useSelector((state) => state.AuthReducer?.user);

  useEffect(() => {
    axios.defaults.timeout = 30000;

    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        console.log(`🔄 ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        console.log(`✅ ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.log(`⚠️ Erreur API ${error.response?.status}: ${error.config?.url}`);
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === 'string') return loc.trim();
    if (loc.$oid) return loc.$oid.toString();
    if (loc._id) return loc._id.toString();
    if (loc.id) return loc.id.toString();
    return null;
  };

  // 🔄 Charger les produits
  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/ProductV/getProductV?management=true", {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          timeout: 15000
        });

        if (isMounted) {
          setProducts(response.data);
        }
      } catch (error) {
        console.error("Erreur chargement produits:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  // 🆕 Charger les localisations
  useEffect(() => {
    let isMounted = true;

    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations", {
          timeout: 15000
        });

        if (isMounted) {
          setLocations(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error("Erreur chargement emplacements:", err);
        setLocations([]);
      }
    };

    fetchLocations();

    return () => {
      isMounted = false;
    };
  }, []);

  // 🔥 CORRECTION : Définir les localisations par défaut - useradmin = admin
  useEffect(() => {
    if (currentUser?.role === "admin" || currentUser?.role === "useradmin") {
      return;
    }

    if (currentUser?.localisation) {
      const userLocId = extractLocId(currentUser.localisation._id);
      setSelectedLocations([userLocId]);
    }
  }, [currentUser]);

  // Charger les sous-produits SubProductV pour composer un SET
  // Filtrer : catégorie VALISES (via produit parent) + localisation BNG-Tunisia Mall
  useEffect(() => {
    const fetchSubProductsForSet = async () => {
      try {
        const res = await axios.get("/api/ProductV/getAllSubProductV", {
          timeout: 30000,
        });
        const rawList = Array.isArray(res.data?.subProducts) ? res.data.subProducts : [];
        // Id de la localisation BNG-Tunisia Mall (depuis la liste des locations déjà chargée)
        const bngLoc = locations.find(
          (l) => (l.name || "").trim().toUpperCase() === "BNG-TUNISIA MALL"
        );
        const bngId = bngLoc ? extractLocId(bngLoc._id) : null;
        const list = bngId
          ? rawList.filter((sp) => {
              const spLocId = extractLocId(sp.localisation?._id || sp.localisation);
              return spLocId === bngId;
            })
          : rawList;
        setAllSubProductsForSet(list);
      } catch (err) {
        console.error("Erreur chargement sous-produits pour SET:", err);
        setAllSubProductsForSet([]);
      }
    };
    fetchSubProductsForSet();
  }, [locations]);

  const toggleSetSubProduct = (id) => {
    setSelectedSetSubProducts((prev) =>
      prev.includes(id) ? prev.filter((sp) => sp !== id) : [...prev, id]
    );
  };

  const addSetProductHandler = async () => {
    if (
      !newProductName.trim() ||
      !setPrice ||
      selectedSetSubProducts.length !== 3
    ) {
      alert(
        "Pour un SET : nom, prix et exactement 3 sous-produits (1 GRAND, 1 MOYEN, 1 PETIT) sont requis."
      );
      return;
    }
    const priceNum = parseFloat(setPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      alert("Le prix du SET doit être un nombre positif.");
      return;
    }
    // Lire le fichier depuis la ref (toujours à jour) ou le state — le bouton doit bien prendre l'image en compte
    const fileToUse = setImageFileRef.current ?? setImageFile;
    console.log("[ProductValises] addSetProductHandler — image prise en compte:", fileToUse ? { name: fileToUse.name, size: fileToUse.size } : "non");
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      // Préparer l'image du SET en base64 (max ~128 KB) — même logique que UpdateProduct
      let base64Image = "";
      if (fileToUse) {
        try {
          base64Image = await compressImage(fileToUse, 128);
          console.log("[ProductValises] SET image compressée:", base64Image.length, "caractères");
        } catch (err) {
          console.error("[ProductValises] Compression image SET:", err);
          alert("Impossible de traiter l'image. Utilisez une image valide (JPG/PNG).");
          setIsLoading(false);
          return;
        }
      } else {
        console.log("[ProductValises] Création SET sans image (setImageFile vide)");
      }

      // Créer le SET dans ProductV avec subProductsV (SubProductV)
      const payload = {
        name: newProductName.trim(),
        category: "SET",
        subProductsV: selectedSetSubProducts,
        price: priceNum,
        image: base64Image,
      };
      console.log("[ProductValises] Envoi SET payload — image length:", payload.image?.length ?? 0);
      const response = await axios.post("/api/ProductV/AddProductV", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      setNewProductName("");
      setSetPrice("");
      setSelectedSetSubProducts([]);
      setSetImageFile(null);
      setSetImagePreview(null);
      setImageFileRef.current = null;
      if (setImageInputRef.current) setImageInputRef.current.value = "";
      setNewProductCategory("VALISES");
      const created = response.data;
      console.log("[ProductValises] SET créé — réponse API image:", created?.image ? `${created.image.length} car.` : "absente");
      setProducts((prev) => [...prev, created]);
      if (onProductAdded) onProductAdded(created);
      alert("✅ SET créé avec succès !");
    } catch (error) {
      console.error("[ProductValises] Erreur création SET:", error?.response?.data || error);
      alert(
        "❌ Erreur lors de la création du SET. " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableLocations = () => {
    if (!currentUser) return [];
    if (!Array.isArray(locations)) return [];

    if (currentUser.role === "admin" || currentUser.role === "useradmin") {
      return locations;
    } else {
      const userLocId = extractLocId(currentUser.localisation?._id);
      return locations.filter(loc => {
        const locId = extractLocId(loc._id);
        return locId === userLocId;
      });
    }
  };

  const availableLocations = getAvailableLocations();

  const handleLocationChange = (locationId) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const toggleAllLocations = () => {
    if (selectedLocations.length === availableLocations.length) {
      setSelectedLocations([]);
    } else {
      const allLocationIds = availableLocations.map(loc => extractLocId(loc._id)).filter(id => id !== null);
      setSelectedLocations(allLocationIds);
    }
  };

  const compressImage = (file, targetKB) => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type.startsWith("image/")) {
        reject(new Error("Fichier image invalide"));
        return;
      }
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error("Chargement de l'image impossible"));
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const MAX_DIM = 800;
          if (width > height && width > MAX_DIM) {
            height = (height * MAX_DIM) / width;
            width = MAX_DIM;
          } else if (height > MAX_DIM) {
            width = (width * MAX_DIM) / height;
            height = MAX_DIM;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.8;
          let base64 = canvas.toDataURL("image/jpeg", quality);
          const maxLen = targetKB * 1.33 * 1024;
          if (base64.length > maxLen) {
            quality = 0.5;
            base64 = canvas.toDataURL("image/jpeg", quality);
          }
          if (base64.length > maxLen) {
            quality = 0.35;
            base64 = canvas.toDataURL("image/jpeg", quality);
          }
          resolve(base64);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const addProductHandler = async () => {
    if (!newProductName.trim()) {
      alert("Veuillez entrer un nom pour le produit.");
      return;
    }

    try {
      setIsLoading(true);
      let base64Image = "";
      if (newSubProductImage) {
        base64Image = await compressImage(newSubProductImage, 128);
      }
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/ProductV/AddProductV", {
        name: newProductName,
        category: newProductCategory,
        image: base64Image
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        }
      });

      setNewProductName("");
      setNewProductCategory("VALISES");
      setNewSubProductImage(null);
      setNewSubProductImagePreview(null);
      if (subProductImageInputRef.current) subProductImageInputRef.current.value = "";
      setProducts((prev) => [...prev, response.data]);
      if (onProductAdded) onProductAdded(response.data);
      alert("✅ Produit ajouté avec succès !");
    } catch (error) {
      console.error("Erreur ajout produit:", error);
      alert("❌ Erreur lors de l'ajout du produit.");
    } finally {
      setIsLoading(false);
    }
  };

  const checkBarcodeInLocation = (barcode, locationId) => {
    if (!barcode.trim()) return false;
    const existsInSameLocation = products.some(product =>
      product.subProductsV?.some(subProduct => {
        const subProductLocations = subProduct.localisations || [subProduct.localisation].filter(Boolean);
        const hasSameLocation = subProductLocations.some(loc =>
          extractLocId(loc) === locationId
        );
        return subProduct.barcode === barcode && hasSameLocation;
      })
    );
    return existsInSameLocation;
  };

  const addSubProductHandler = async () => {
    if (!selectedProduct || !newSubProductName.trim() || !newSubProductPrice || selectedLocations.length === 0) {
      alert("Veuillez sélectionner un produit, entrer un nom, un prix et sélectionner au moins une localisation.");
      return;
    }

    const price = parseFloat(newSubProductPrice);
    if (isNaN(price) || price <= 0) {
      alert("❌ Le prix doit être un nombre positif.");
      return;
    }

    const validLocations = selectedLocations.filter(locId => {
      return availableLocations.some(loc => extractLocId(loc._id) === locId);
    });

    if (validLocations.length === 0) {
      alert("❌ Aucune localisation valide sélectionnée.");
      return;
    }

    const barcodeTrimmed = newSubProductBarcode.trim();
    const locationsWithBarcodeIssues = [];

    if (barcodeTrimmed) {
      validLocations.forEach(locationId => {
        if (checkBarcodeInLocation(barcodeTrimmed, locationId)) {
          const location = availableLocations.find(loc => extractLocId(loc._id) === locationId);
          locationsWithBarcodeIssues.push(location?.name || locationId);
        }
      });

      if (locationsWithBarcodeIssues.length > 0) {
        alert(`❌ Ce code-barres existe déjà dans les localisations suivantes: ${locationsWithBarcodeIssues.join(', ')}\n\nUn même code-barres ne peut pas être utilisé dans la même localisation.`);
        return;
      }
    }

    try {
      setIsLoading(true);
      let base64Image = "";
      if (newSubProductImage) {
        base64Image = await compressImage(newSubProductImage, 128);
      }

      const token = localStorage.getItem("token");
      const results = [];

      for (const locationId of validLocations) {
        try {
          const payload = {
            name: newSubProductName.trim(),
            price: price,
            barcode: barcodeTrimmed || "",
            localisation: locationId,
            image: base64Image
          };

          const response = await axios.post(
            `/api/ProductV/${selectedProduct}/subproduct`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : ""
              },
              timeout: 30000
            }
          );
          results.push({ success: true, locationId, data: response.data });
        } catch (error) {
          results.push({
            success: false,
            locationId,
            error: error.response?.data?.message || error.message
          });
        }
      }

      const successful = results.filter(r => r.success);
      if (successful.length > 0) {
        setNewSubProductName("");
        setNewSubProductPrice("");
        setNewSubProductBarcode("");
        setNewSubProductImage(null);
        setNewSubProductImagePreview(null);
        if (subProductImageInputRef.current) subProductImageInputRef.current.value = "";
        if (currentUser?.role !== "admin" && currentUser?.role !== "useradmin") {
          setSelectedLocations([]);
        }
        if (onSubProductAdded) onSubProductAdded();
        alert(`✅ ${successful.length} sous-produit(s) ajouté(s) !`);

        // Rafraîchir les produits
        const refreshResponse = await axios.get("/api/ProductV/getProductV?management=true", {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
          timeout: 15000
        });
        setProducts(refreshResponse.data);
      }
    } catch (globalError) {
      alert("❌ Erreur critique lors de la création.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBarcodeTest = async () => {
    if (!newSubProductBarcode.trim()) {
      alert("Veuillez entrer un code-barres à tester");
      return;
    }
    try {
      const response = await axios.get(`/api/ProductV/findByBarcode/${newSubProductBarcode.trim()}`, {
        headers: { Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "" }
      });
      if (response.data) {
        alert(`✅ Produit trouvé: ${response.data.name} - ${response.data.price}€`);
        setNewSubProductName(response.data.name);
        setNewSubProductPrice(response.data.price.toString());
      } else {
        alert("❌ Aucun produit trouvé.");
      }
    } catch (e) {
      alert("❌ Aucun produit trouvé ou erreur serveur.");
    }
  };

  if (!currentUser) return null;

  return (
    <div className="product-manager pv-two-columns">
      <div className="user-info">
        <small><strong>{currentUser.name}</strong> ({currentUser.role}) - {currentUser.localisation?.name || "Sans lieu"}</small>
      </div>

      {isLoading && <div className="loading-overlay">Chargement...</div>}

      <div className="pv-grid-container">
        {/* Colonne Gauche : Produit */}
        <div className="pv-column">
          <h3>Ajouter un Produit</h3>
          <input type="text" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} placeholder="Nom du produit" />
          <select value={newProductCategory} onChange={(e) => { setNewProductCategory(e.target.value); if (e.target.value !== "SET") { setSelectedSetSubProducts([]); setSetPrice(""); } }}>
            <option value="VALISES">VALISES</option>
            <option value="SACS">SACS</option>
            <option value="ACCESSOIRES">ACCESSOIRES</option>
            <option value="SET">SET</option>
            <option value="TRANSPORT">TRANSPORT</option>
            <option value="POUPEE">POUPEE</option>
            <option value="EDUCATIFS">EDUCATIFS</option>
            <option value="METIERS">METIERS</option>
            <option value="BRICOLAGE">BRICOLAGE</option>
            <option value="PERSONNAGE">PERSONNAGE</option>
            <option value="ANIMAUX">ANIMAUX</option>
            <option value="FILLETTE">FILLETTE</option>
            <option value="CUISINE">CUISINE</option>
            <option value="MUSIQUE">MUSIQUE</option>
            <option value="FIRSTAGE">FIRSTAGE</option>
            <option value="CANDY STORE">CANDY STORE</option>
            <option value="PLAYSTATION">PLAYSTATION</option>
          </select>

          {newProductCategory === "SET" && (
            <div className="set-composition">
              <h4 className="set-composition-title">Composition du SET (GRAND + MOYEN + PETIT)</h4>
              <input
                type="number"
                step="0.01"
                min="0"
                value={setPrice}
                onChange={(e) => setSetPrice(e.target.value)}
                placeholder="Prix du SET"
              />
              <div className="pv-form-group set-image-field">
                <label>Image du SET (sera compressée)</label>
                <input
                  ref={setImageInputRef}
                  type="file"
                  accept="image/*"
                  className="pv-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setImageFileRef.current = file; // toujours à jour pour le clic "Créer le SET"
                    console.log("[ProductValises] SET input file onChange — fichier:", file ? { name: file.name, size: file.size, type: file.type } : "aucun");
                    setSetImageFile(file);
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => setSetImagePreview(reader.result);
                      reader.readAsDataURL(file);
                    } else {
                      setSetImagePreview(null);
                    }
                  }}
                />
                {setImagePreview && (
                  <div className="set-image-preview">
                    <img src={setImagePreview} alt="Aperçu SET" />
                    <button type="button" className="pv-btn-image pv-btn-remove" onClick={() => { setSetImageFile(null); setSetImagePreview(null); setImageFileRef.current = null; if (setImageInputRef.current) setImageInputRef.current.value = ""; }}>Retirer l'image</button>
                  </div>
                )}
              </div>
              <div className="set-subproducts-grid">
                {allSubProductsForSet.map((sp) => (
                  <button
                    key={sp._id}
                    type="button"
                    className={`pv-set-choice-btn ${selectedSetSubProducts.includes(sp._id) ? "selected" : ""}`}
                    onClick={() => toggleSetSubProduct(sp._id)}
                  >
                    {sp.name}
                  </button>
                ))}
              </div>
              <small className="set-composition-hint">
                Sélectionnez exactement 3 valises : 1 GRAND, 1 MOYEN, 1 PETIT (ex. SET BLANC).
              </small>
            </div>
          )}

          <button
            type="button"
            className="add-subproduct-btn"
            onClick={newProductCategory === "SET" ? addSetProductHandler : addProductHandler}
            disabled={isLoading || (newProductCategory === "SET" && selectedSetSubProducts.length !== 3)}
          >
            {newProductCategory === "SET" ? "Créer le SET" : "Ajouter Produit"}
          </button>
        </div>

        {/* Colonne Droite : Sous-Produit */}
        <div className="pv-column">
          <h3>Ajouter un Sous-Produit</h3>
          <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
            <option value="">Sélectionner un produit</option>
            {products.map((p) => <option key={p._id} value={p._id}>{p.name} ({p.category})</option>)}
          </select>

          <div className="barcode-section">
            <input type="text" value={newSubProductBarcode} onChange={(e) => setNewSubProductBarcode(e.target.value)} placeholder="Code-barres" />
            <button type="button" onClick={handleBarcodeTest} className="test-barcode-btn">🔍 Tester</button>
          </div>

          <input type="text" value={newSubProductName} onChange={(e) => setNewSubProductName(e.target.value)} placeholder="Nom du sous-produit" />
          <input type="number" value={newSubProductPrice} onChange={(e) => setNewSubProductPrice(e.target.value)} placeholder="Prix" />
          <div className="pv-image-upload" style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: "500", color: "#333" }}>Image du sous-produit</label>
            <input
              ref={subProductImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setNewSubProductImage(file);
                if (file) {
                  const reader = new FileReader();
                  reader.onload = () => setNewSubProductImagePreview(reader.result);
                  reader.readAsDataURL(file);
                } else {
                  setNewSubProductImagePreview(null);
                }
              }}
            />
            <button
              type="button"
              className="pv-btn-image"
              onClick={() => subProductImageInputRef.current?.click()}
              style={{
                padding: "10px 16px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                background: "#fff",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              📷 Choisir une image
            </button>
            {newSubProductImagePreview && (
              <div style={{ marginTop: "8px" }}>
                <img src={newSubProductImagePreview} alt="Aperçu" style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }} />
                <button type="button" onClick={() => { setNewSubProductImage(null); setNewSubProductImagePreview(null); if (subProductImageInputRef.current) subProductImageInputRef.current.value = ""; }} style={{ marginLeft: "8px", fontSize: "12px", padding: "4px 8px" }}>Retirer</button>
              </div>
            )}
          </div>

          <div className="locations-section">
            <div className="locations-header">
              <h4>Localisations *</h4>
              <button type="button" onClick={toggleAllLocations}>{selectedLocations.length === availableLocations.length ? 'Tout désélectionner' : 'Tout sélectionner'}</button>
            </div>
            <div className="locations-grid">
              {availableLocations.map(loc => {
                const id = extractLocId(loc._id);
                const isSelected = selectedLocations.includes(id);
                return (
                  <div key={id} className={`location-card ${isSelected ? 'selected' : ''}`} onClick={() => handleLocationChange(id)}>
                    {loc.name}
                  </div>
                );
              })}
            </div>
          </div>

          <button className="add-subproduct-btn" onClick={addSubProductHandler} disabled={isLoading || selectedLocations.length === 0}>
            Créer dans {selectedLocations.length} lieu(x)
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductValises;
